import { camelize } from 'humps';
import type { PatientStatus, PatientStatusBucket, PatientStatusHint } from '@/api/patients';

type Translate = (key: string, params?: Record<string, unknown>) => string;

const DAYS_PER_MONTH = 30;
const DAYS_PER_YEAR = 365;

/** ISO date (YYYY-MM-DD) or ISO datetime → dd/mm/yyyy; '' when absent. */
export function formatShortDate(value: string | null | undefined): string {
  if (!value) return '';
  const [datePart] = value.split('T');
  const [year, month, day] = datePart.split('-');
  if (!year || !month || !day) return '';

  return `${day}/${month}/${year}`;
}

function unitAgo(count: number, unit: 'day' | 'month' | 'year', t: Translate): string {
  return count === 1 ?
    t(`patientStatus.ago.${unit}`) :
    t(`patientStatus.ago.${unit}s`, { count });
}

const YEARS_UNIT = { unit: 'year' as const, below: Infinity, divisor: DAYS_PER_YEAR };
const AGO_UNITS: Array<{ unit: 'day' | 'month' | 'year'; below: number; divisor: number }> = [
  { unit: 'day', below: DAYS_PER_MONTH, divisor: 1 },
  { unit: 'month', below: DAYS_PER_YEAR, divisor: DAYS_PER_MONTH },
  YEARS_UNIT,
];

/** "hoy" / "hace N días" / "hace N meses" / "hace N años" for a non-negative day count. */
export function humanizeDaysAgo(days: number | null | undefined, t: Translate): string {
  if (days === null || days === undefined || days < 0) return '';
  if (days === 0) return t('patientStatus.ago.today');
  const { unit, divisor } = AGO_UNITS.find((candidate) => days < candidate.below) ?? YEARS_UNIT;

  return unitAgo(Math.floor(days / divisor), unit, t);
}

export function bucketOf(status: PatientStatus | null | undefined): PatientStatusBucket {
  return status?.bucket ?? 'active';
}

// Active hints interpolate the hint date; inactive hints interpolate how long ago.
const INACTIVE_HINTS: PatientStatusHint[] = ['requirement_ended', 'no_recent_shift', 'never_served'];
// "Sin atención hace 0 días" would be a lie for someone attended today: what makes them
// inactive is that nothing is planned ahead, so those hints get their own zero-day wording.
const TODAY_HINTS: PatientStatusHint[] = ['requirement_ended', 'no_recent_shift'];

function hintKey(status: PatientStatus): string {
  if (status.hint === 'requirement_open' && !status.hintDate) return 'requirementOpenEnded';
  const camel = camelize(status.hint as string);
  const isToday = status.inactiveDays === 0 && TODAY_HINTS.includes(status.hint!);

  return isToday ? `${camel}Today` : camel;
}

/** Row hint: "Req. hasta 30/09", "Sin requerimiento hace 4 días", "Nuevo · ingresó hace 2 días". */
export function statusLabel(status: PatientStatus | null | undefined, t: Translate): string {
  if (!status?.hint) return '';
  const params = INACTIVE_HINTS.includes(status.hint) ?
    { ago: humanizeDaysAgo(status.inactiveDays, t) } :
    { date: formatShortDate(status.hintDate) };

  return t(`patientStatus.hint.${hintKey(status)}`, params);
}

// Estado chip (list rows and the profile header): Activos read a plain "Activo" with the
// reason in a tooltip; Inactivos read the reason itself, with "Nuevo" in blue so
// onboarding rows stand apart from the truly unattended orange ones.
export function statusChipColor(status: PatientStatus | null | undefined): string {
  if (bucketOf(status) === 'active') return 'success';

  return status?.hint === 'never_served' ? 'info' : 'warning';
}

export function statusChipLabel(
  status: PatientStatus | null | undefined,
  t: Translate,
): string {
  if (bucketOf(status) === 'active') return t('patientStatus.bucket.active');

  return statusLabel(status, t);
}

export function statusChipTooltip(
  status: PatientStatus | null | undefined,
  t: Translate,
): string {
  if (bucketOf(status) !== 'active') return '';
  // Any requirement-driven status gets the fixed copy; the other reasons
  // (scheduled shift, service end date) keep their specific label.
  if (status?.hint === 'requirement_open') return t('patientStatus.activeRequirementTooltip');

  return statusLabel(status, t);
}

